/**
 * Clinic Location Manager Detail Modal
 *
 * 施術管理者詳細 - 拠点管理 (Modal)
 */

import React, { useMemo } from 'react';
import { ClinicLocationManager } from '@/pages/main/clinicLocation/types';
import { AppModal } from '@/components/atoms';
import { InfoRow, ScheduleTable } from '@/components/molecules';
import { formatPeriodDisplay } from '@/utils/dateConverter';
import { renderTableCellTruncate } from '@/utils/tableCellTruncate';
import { SERVICE_TYPE_A2 } from '@/constants/serviceType';

interface ClinicLocationManagerDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  managerData: ClinicLocationManager | null;
  /** A2 or R2 - from page context (e.g. useTheme). Drives title/label (はり・きゅう師 / マッサージ師). */
  serviceType: string;
}

const ClinicLocationManagerDetailModal: React.FC<ClinicLocationManagerDetailModalProps> = ({
  isOpen,
  onClose,
  managerData,
  serviceType,
}) => {
  const manager = managerData;
  const isA2 = String(serviceType).toUpperCase() === SERVICE_TYPE_A2;
  const personTypeLabel = isA2 ? 'はり・きゅう師' : 'マッサージ師';

  // Prepare schedule data for ScheduleTable
  const scheduleData = useMemo(() => {
    if (!manager) return null;
    return {
      am: {
        monday: !!manager.is_am_monday,
        tuesday: !!manager.is_am_tuesday,
        wednesday: !!manager.is_am_wednesday,
        thursday: !!manager.is_am_thursday,
        friday: !!manager.is_am_friday,
        saturday: !!manager.is_am_saturday,
        sunday: !!manager.is_am_sunday,
      },
      pm: {
        monday: !!manager.is_pm_monday,
        tuesday: !!manager.is_pm_tuesday,
        wednesday: !!manager.is_pm_wednesday,
        thursday: !!manager.is_pm_thursday,
        friday: !!manager.is_pm_friday,
        saturday: !!manager.is_pm_saturday,
        sunday: !!manager.is_pm_sunday,
      },
    };
  }, [manager]);

  return (
    <AppModal
      isOpen={isOpen}
      onClose={onClose}
      title={`施術管理者詳細`}
      maxWidth="max-w-4xl"
      footer={null}
    >
      <div className="space-y-1">
        <InfoRow
          label={personTypeLabel}
          value={
            manager?.massager
              ? renderTableCellTruncate(
                  `${manager.massager.serial_number}:${manager.massager.name}`,
                  { lineClamp: 2, width: 560 }
                )
              : undefined
          }
        />
        <InfoRow
          label="有効期限"
          value={formatPeriodDisplay(manager?.available_from, manager?.available_to) || undefined}
        />
        <InfoRow label="登録記号番号" value={manager?.location_manager_number} />

        {/* 勤務スケジュール merged here */}
        <div className="pt-6 mt-6">
          {scheduleData && (
            <ScheduleTable
              amTimeStart={manager?.am_time_start}
              amTimeEnd={manager?.am_time_end}
              pmTimeStart={manager?.pm_time_start}
              pmTimeEnd={manager?.pm_time_end}
              schedule={scheduleData}
            />
          )}
        </div>
      </div>
    </AppModal>
  );
};

export default ClinicLocationManagerDetailModal;
